/* ==========================================================================
   THEME.JS — JavaScript global
   Boutique Mode Femme — Thème Shopify 2.0
   ========================================================================== */

'use strict';

/* Announcement bar — slider automatique
   ========== */
class AnnouncementSlider {
  constructor(el) {
    this.el = el;
    this.slides = el.querySelectorAll('.announcement-bar__slide');
    this.current = 0;
    this.interval = parseInt(el.dataset.interval) || 4000;
    if (this.slides.length > 1) this.init();
  }

  init() {
    this.slides[0].classList.add('is-active');
    setInterval(() => this.next(), this.interval);
  }

  next() {
    this.slides[this.current].classList.remove('is-active');
    this.current = (this.current + 1) % this.slides.length;
    this.slides[this.current].classList.add('is-active');
  }
}

/* Header — comportement sticky et transparence
   ========== */
class Header {
  constructor() {
    this.header = document.querySelector('.site-header');
    if (!this.header) return;
    this.scrollThreshold = 60;
    this.lastScroll = 0;
    this.init();
  }

  init() {
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }

  onScroll() {
    const current = window.scrollY;
    if (current > this.scrollThreshold) {
      this.header.classList.add('is-scrolled');
    } else {
      this.header.classList.remove('is-scrolled');
    }
    this.lastScroll = current;
  }
}

/* Mega menu — desktop
   ========== */
class MegaMenu {
  constructor() {
    this.triggers = document.querySelectorAll('.mega-menu__trigger');
    this.overlay = document.querySelector('.mega-menu-overlay');
    this.currentPanel = null;
    this.closeTimeout = null;
    this.init();
  }

  init() {
    this.triggers.forEach(trigger => {
      const panelId = trigger.dataset.panel;
      const panel = document.querySelector(`[data-panel-id="${panelId}"]`);
      if (!panel) return;

      trigger.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        this.open(panel);
      });

      trigger.addEventListener('mouseleave', () => {
        this.closeTimeout = setTimeout(() => this.close(), 150);
      });

      panel.addEventListener('mouseenter', () => clearTimeout(this.closeTimeout));
      panel.addEventListener('mouseleave', () => {
        this.closeTimeout = setTimeout(() => this.close(), 150);
      });
    });
  }

  open(panel) {
    if (this.currentPanel && this.currentPanel !== panel) {
      this.currentPanel.classList.remove('is-active');
    }
    panel.classList.add('is-active');
    this.currentPanel = panel;
    this.overlay && this.overlay.classList.add('is-active');
  }

  close() {
    if (this.currentPanel) {
      this.currentPanel.classList.remove('is-active');
      this.currentPanel = null;
    }
    this.overlay && this.overlay.classList.remove('is-active');
  }
}

/* Menu mobile — géré directement dans sections/mobile-menu.liquid */

/* Cart drawer
   ========== */
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.overlay');
    this.countEls = document.querySelectorAll('.cart-count');
    if (!this.drawer) return;
    this.init();
  }

  init() {
    document.addEventListener('click', e => {
      if (e.target.closest('.btn-open-cart')) this.open();
    });

    this.drawer.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.refresh());

    /* Recommandations initiales à partir du dernier produit ajouté (rendu serveur),
       sans attendre une ouverture du drawer ou un fetch /cart.js superflu. */
    document.querySelectorAll('[data-cart-recommendations]').forEach(container => {
      const baseProductId = container.dataset.baseProductId;
      if (baseProductId) this.loadRecommendations(container, baseProductId);
    });
  }

  open() {
    this.refresh();
    this.drawer.classList.add('is-active');
    this.overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('is-active');
    this.overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.updateCount(cart.item_count);
      this.updateItems(cart);
      this.updateShipping(cart);
      this.updateRecommendations(cart);
    } catch (e) {
      console.error('Cart refresh error:', e);
    }
  }

  updateShipping(cart) {
    document.querySelectorAll('[data-cart-shipping]').forEach(el => {
      const threshold = parseInt(el.dataset.thresholdCents, 10) || 0;
      const text = el.querySelector('.cart-drawer__shipping-text, [data-cart-shipping-text]') || el.querySelector('p');
      const fill = el.querySelector('.cart-drawer__shipping-bar-fill, [data-cart-shipping-fill]') || el.querySelector('[class*="shipping-bar-fill"]');
      const remaining = threshold - cart.total_price;
      const percent = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 100;

      if (text) {
        text.innerHTML = remaining > 0
          ? el.dataset.remainingTemplate.replace('[[amount]]', `<span class="cart-drawer__shipping-amount">${this.escapeHtml(this.formatMoney(remaining))}</span>`)
          : this.escapeHtml(el.dataset.unlockedText);
      }
      if (fill) fill.style.width = percent + '%';
    });
  }

  updateRecommendations(cart) {
    const containers = document.querySelectorAll('[data-cart-recommendations]');
    const baseProductId = cart.items.length ? cart.items[cart.items.length - 1].product_id : null;

    containers.forEach(container => {
      if (!baseProductId) {
        container.style.display = 'none';
        container.dataset.loaded = '';
        return;
      }
      this.loadRecommendations(container, baseProductId);
    });
  }

  async loadRecommendations(container, baseProductId) {
    if (container.dataset.baseProductId === String(baseProductId) && container.dataset.loaded === 'true') {
      return;
    }

    try {
      const res = await fetch(`/recommendations/products.json?product_id=${baseProductId}&limit=8&intent=related`);
      const data = await res.json();
      const products = data.products || [];
      const track = container.querySelector('.cart-drawer__recommendations-track, [data-cart-recommendations-track]');

      if (!products.length || !track) {
        container.style.display = 'none';
        return;
      }

      track.innerHTML = products.map(p => `
        <a href="${p.url}" class="cart-upsell-card">
          <span class="cart-upsell-card__media">
            ${p.featured_image ? `<img src="${this.resizeImage(p.featured_image, 400)}" alt="${this.escapeHtml(p.title)}" loading="lazy">` : ''}
          </span>
          <span class="cart-upsell-card__info">
            <span class="cart-upsell-card__title">${this.escapeHtml(p.title)}</span>
            <span class="cart-upsell-card__price">${this.formatMoney(p.price)}</span>
          </span>
        </a>
      `).join('');

      container.dataset.baseProductId = String(baseProductId);
      container.dataset.loaded = 'true';
      container.style.display = '';
    } catch (e) {
      console.error('Cart recommendations error:', e);
      container.style.display = 'none';
    }
  }

  updateCount(count) {
    this.countEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  updateItems(cart) {
    const list = this.drawer.querySelector('.cart-drawer__items');
    const subtotal = this.drawer.querySelector('.cart-drawer__subtotal-amount');
    const empty = this.drawer.querySelector('.cart-drawer__empty');
    const actions = this.drawer.querySelector('.cart-drawer__actions');

    if (!list) return;

    if (cart.item_count === 0) {
      list.innerHTML = '';
      empty && (empty.style.display = '');
      actions && (actions.style.display = 'none');
    } else {
      empty && (empty.style.display = 'none');
      actions && (actions.style.display = '');
      list.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-key="${item.key}">
          <a href="${item.url}" class="cart-item__image">
            <img src="${item.image}" alt="${item.product_title}" width="80" loading="lazy">
          </a>
          <div class="cart-item__details">
            <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
            ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <div class="cart-item__footer">
              <div class="cart-item__qty">
                <button class="qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity - 1}">−</button>
                <span>${item.quantity}</span>
                <button class="qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity + 1}">+</button>
              </div>
              <span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
          <button class="cart-item__remove" data-key="${item.key}" aria-label="Supprimer">×</button>
        </div>
      `).join('');
    }

    if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price);

    list.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', () => this.updateItem(btn.dataset.key, 0));
    });

    list.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', () => this.updateItem(btn.dataset.key, parseInt(btn.dataset.qty)));
    });
  }

  async updateItem(key, qty) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      this.refresh();
    } catch (e) {
      console.error('Update cart error:', e);
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }

  resizeImage(url, width) {
    if (!url) return '';
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, (match, ext, query) => `_${width}x.${ext}${query || ''}`);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

/* Add to cart
   ========== */
class AddToCart {
  constructor() {
    document.addEventListener('submit', e => {
      const form = e.target.closest('.product-form');
      if (!form) return;
      e.preventDefault();
      this.submit(form);
    });
  }

  async submit(form) {
    /* Le bouton de soumission vit désormais hors du <form> (barre sticky),
       relié uniquement via l'attribut form="" — form.querySelector ne le
       trouve plus, il faut le chercher dans tout le document. */
    const formId = form.getAttribute('id');
    const btn = form.querySelector('[type="submit"]') ||
      document.querySelector('[type="submit"][form="' + formId + '"]');
    if (!btn || btn.disabled) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Ajout en cours...';

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      if (!res.ok) throw new Error('Add to cart failed');

      btn.textContent = '✓ Ajouté !';
      document.dispatchEvent(new CustomEvent('cart:open'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2500);
    } catch (e) {
      btn.textContent = originalText;
      btn.disabled = false;
      console.error('Add to cart error:', e);
    }
  }
}

/* Product gallery — mobile swipe & desktop thumbnails
   ========== */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.main = el.querySelector('.product-gallery__main');
    this.thumbs = el.querySelectorAll('.product-gallery__thumb');
    this.images = el.querySelectorAll('.product-gallery__img');
    this.current = 0;
    this.startX = 0;
    this.init();
  }

  init() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i));
    });

    this.main?.addEventListener('touchstart', e => { this.startX = e.touches[0].clientX; }, { passive: true });
    this.main?.addEventListener('touchend', e => {
      const diff = this.startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) diff > 0 ? this.next() : this.prev();
    });
  }

  goTo(index) {
    this.images[this.current]?.classList.remove('is-active');
    this.thumbs[this.current]?.classList.remove('is-active');
    this.current = index;
    this.images[this.current]?.classList.add('is-active');
    this.thumbs[this.current]?.classList.add('is-active');
  }

  next() { this.goTo((this.current + 1) % this.images.length); }
  prev() { this.goTo((this.current - 1 + this.images.length) % this.images.length); }
}

/* Variant selector
   ========== */
class VariantSelector {
  constructor(form) {
    this.form = form;
    this.selects = form.querySelectorAll('.variant-option');
    this.productData = JSON.parse(form.dataset.product || '{}');
    this.priceEl = document.querySelector('.product-price__current');
    this.comparePriceEl = document.querySelector('.product-price__compare');
    this.addBtn = form.querySelector('[type="submit"]');
    this.init();
  }

  init() {
    this.selects.forEach(select => {
      select.addEventListener('change', () => this.update());
    });
    this.update();
  }

  getCurrentVariant() {
    const values = Array.from(this.selects).map(s => s.value);
    return this.productData.variants?.find(v =>
      v.options.every((opt, i) => opt === values[i])
    );
  }

  update() {
    const variant = this.getCurrentVariant();
    if (!variant) return;

    const input = this.form.querySelector('[name="id"]');
    if (input) input.value = variant.id;

    if (this.priceEl) {
      this.priceEl.textContent = this.formatMoney(variant.price);
    }

    if (this.comparePriceEl) {
      if (variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    if (this.addBtn) {
      if (!variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Épuisé';
      } else {
        this.addBtn.disabled = false;
        this.addBtn.textContent = 'Ajouter au panier';
      }
    }

    history.replaceState(null, '', `?variant=${variant.id}`);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }
}

/* Product carousel — flèches et drag
   ========== */
class ProductCarousel {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.carousel__track');
    this.btnPrev = el.querySelector('.carousel__btn--prev');
    this.btnNext = el.querySelector('.carousel__btn--next');
    if (!this.track) return;
    this.init();
  }

  init() {
    this.btnPrev?.addEventListener('click', () => this.scroll(-1));
    this.btnNext?.addEventListener('click', () => this.scroll(1));
    this.updateButtons();
    this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });
  }

  scroll(dir) {
    const cardWidth = this.track.querySelector('.product-card')?.offsetWidth || 300;
    this.track.scrollBy({ left: dir * (cardWidth + 16), behavior: 'smooth' });
  }

  updateButtons() {
    if (!this.btnPrev || !this.btnNext) return;
    this.btnPrev.disabled = this.track.scrollLeft <= 0;
    this.btnNext.disabled = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 1;
  }
}

/* Modal guide des tailles
   ========== */
class SizeGuideModal {
  constructor() {
    this.modal = document.querySelector('.size-guide-modal');
    this.trigger = document.querySelector('.size-guide-trigger');
    this.close = document.querySelector('.size-guide-modal__close');
    if (!this.modal) return;
    this.init();
  }

  init() {
    this.trigger?.addEventListener('click', e => {
      e.preventDefault();
      this.modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    });
    this.close?.addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', e => {
      if (e.target === this.modal) this.closeModal();
    });
  }

  closeModal() {
    this.modal.classList.remove('is-active');
    document.body.style.overflow = '';
  }
}

/* Accordéons
   ========== */
class Accordion {
  constructor(el) {
    this.triggers = el.querySelectorAll('.accordion__trigger');
    this.triggers.forEach(t => t.addEventListener('click', () => this.toggle(t)));
  }

  toggle(trigger) {
    const item = trigger.closest('.accordion__item');
    const content = item.querySelector('.accordion__content');
    const isOpen = item.classList.contains('is-open');

    item.closest('.accordion')?.querySelectorAll('.accordion__item.is-open').forEach(open => {
      open.classList.remove('is-open');
      open.querySelector('.accordion__content').style.maxHeight = '0';
    });

    if (!isOpen) {
      item.classList.add('is-open');
      content.style.maxHeight = content.scrollHeight + 'px';
    }
  }
}

/* Filtres collection
   ========== */
class CollectionFilters {
  constructor() {
    this.form = document.querySelector('.collection-filters__form');
    if (!this.form) return;
    this.init();
  }

  init() {
    this.form.querySelectorAll('input, select').forEach(input => {
      input.addEventListener('change', () => this.apply());
    });

    document.querySelector('.filters-clear')?.addEventListener('click', () => {
      this.form.reset();
      this.apply();
    });

    document.querySelector('.filters-toggle')?.addEventListener('click', () => {
      document.querySelector('.collection-filters').classList.toggle('is-open');
    });
  }

  apply() {
    const params = new URLSearchParams(new FormData(this.form));
    window.location.search = params.toString();
  }
}

/* Initialisation DOM
   ========== */
document.addEventListener('DOMContentLoaded', () => {
  /* Announcement bar */
  document.querySelectorAll('.announcement-bar').forEach(el => new AnnouncementSlider(el));

  /* Header */
  new Header();

  /* Menus */
  new MegaMenu();

  /* Panier */
  new CartDrawer();
  new AddToCart();

  /* Galerie produit */
  document.querySelectorAll('.product-gallery').forEach(el => new ProductGallery(el));

  /* Variant selector */
  document.querySelectorAll('.product-form').forEach(form => new VariantSelector(form));

  /* Carousels */
  document.querySelectorAll('.product-carousel').forEach(el => new ProductCarousel(el));

  /* Guide tailles */
  new SizeGuideModal();

  /* Accordéons */
  document.querySelectorAll('.accordion').forEach(el => new Accordion(el));

  /* Filtres */
  new CollectionFilters();

  /* Smooth reveal au scroll */
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
  }
});
